import classNames from 'classnames';
import { format } from 'date-fns';
import { useTranslation } from 'next-i18next';
import React, { forwardRef } from 'react';

import { IPropertySettings } from '@/modules/settings/property/types';
import { NextImage } from '@/shared/components/atoms';
import { farmFields } from '@/shared/constant/general';
import { useStore } from '@/shared/hooks/context/createGenericContext';

import AydiLogoText from '~/images/shared/AydiLogoText.svg';

const PrintContent = forwardRef<
  HTMLDivElement,
  { field: IPropertySettings.IPropertyField }
>(({ field }, ref) => {
  const { t } = useTranslation();

  const PrintItem: React.FC<{
    title: string;
    value: string;
    subtitle?: string;
  }> = ({ title, value, subtitle }) => (
    <div>
      <div className='text-10 print:text-20'>{title}</div>
      <div className='text-14 font-semi-bold print:text-32'>{value}</div>
      {subtitle && (
        <div className='mt-1 text-12 text-surface-10 print:text-24'>
          {subtitle}
        </div>
      )}
    </div>
  );

  const isFarmField = farmFields.some((farmType) => farmType === field.type);

  const [companyLogo] = useStore((store) => store?.companyLogo);

  return (
    <div
      ref={ref}
      className='m-auto flex w-full flex-col items-center gap-7 rounded-xl border-xs border-outline-variant py-40 px-24 print:border-0'
    >
      <div className='flex flex-col items-center gap-[15px] print:gap-24'>
        {companyLogo && (
          <NextImage
            priority
            useSkeleton
            fill
            className='h-[44px] w-[60px] print:h-[100px] print:w-full'
            imgClassName='object-contain'
            src={companyLogo}
            alt='company Logo'
          />
        )}
        <div className='flex items-center gap-4'>
          <div className='text-10 font-medium print:text-24'>
            {t('Powered by')}
          </div>
          <div className='w-45 h-20 print:h-auto print:w-100'>
            <AydiLogoText width='100%' height='100%' />
          </div>
        </div>
      </div>
      <NextImage
        priority
        fill
        useSkeleton
        className='h-44 w-full print:h-[50vh]'
        src={field.QRCode || ''}
        alt='qr code'
      />
      <div
        className={classNames(
          'flex w-full items-center gap-16 text-center',
          isFarmField
            ? 'justify-between print:justify-around'
            : 'justify-center'
        )}
      >
        <PrintItem
          title={t('Field')}
          value={field.name}
          subtitle={
            isFarmField ? `${field.area} ${t(field.areaUnit)}` : undefined
          }
        />
        {isFarmField && (
          <PrintItem
            title={t('crop')}
            value={`${field.cropName}${
              field?.varietyName ? ` - ${field.varietyName}` : ''
            }`}
            subtitle={
              field?.plantationDate
                ? format(new Date(field.plantationDate), 'MMM d, yyyy')
                : '-'
            }
          />
        )}
      </div>
    </div>
  );
});

export default PrintContent;
